import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileText, X, Loader2 } from "lucide-react";
import { pdfjs } from "react-pdf";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function countPdfPages(dataUrl) {
  try {
    const task = pdfjs.getDocument({ url: dataUrl });
    const pdf = await task.promise;
    return pdf.numPages;
  } catch {
    return 1;
  }
}

export default function UploadDocument() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [dataUrl, setDataUrl] = useState("");

  const onPick = async (f) => {
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) {
      toast.error("File too large (max 12MB for demo).");
      return;
    }
    if (!/\.(pdf)$/i.test(f.name) && f.type !== "application/pdf") {
      toast.error("Only PDF files are supported in this version.");
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.pdf$/i, ""));
    const url = await readFileAsDataURL(f);
    setDataUrl(url);
  };

  const submit = async () => {
    if (!file || !dataUrl || !title.trim()) {
      toast.error("Please select a PDF and enter a title.");
      return;
    }
    setBusy(true);
    try {
      const pageCount = await countPdfPages(dataUrl);
      const { data } = await api.post("/documents", {
        title: title.trim(),
        file_name: file.name,
        file_data_url: dataUrl,
        page_count: pageCount,
      });
      toast.success("Uploaded. Prepare your document next.");
      navigate(`/app/documents/${data.id}/prepare`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="upload-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">
          Upload
        </div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Upload a document</h1>
        <p className="mt-1 text-sm text-slate-500">
          Drop in a PDF and we'll take you straight into the field editor.
        </p>
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
        <Label className="text-sm font-medium text-slate-700">Document title</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Senior Engineer — Offer Letter"
          className="mt-1.5 h-11"
          data-testid="upload-title-input"
        />

        <div className="mt-6">
          <Label className="text-sm font-medium text-slate-700">Document file (PDF)</Label>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              onPick(e.dataTransfer.files?.[0]);
            }}
            className="mt-1.5 cursor-pointer rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/40 transition p-10 text-center"
            data-testid="upload-dropzone"
          >
            {!file ? (
              <>
                <UploadCloud className="h-8 w-8 mx-auto text-slate-400" />
                <div className="mt-3 text-sm font-medium text-slate-900">
                  Drag & drop your PDF here
                </div>
                <div className="text-xs text-slate-500 mt-1">or click to browse · max 12MB</div>
              </>
            ) : (
              <div className="inline-flex items-center gap-3 rounded-md border border-slate-200 bg-white px-4 py-3">
                <FileText className="h-5 w-5 text-slate-600" />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">{file.name}</div>
                  <div className="text-xs text-slate-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    setDataUrl("");
                  }}
                  className="ml-2 p-1 rounded hover:bg-slate-100"
                >
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="upload-file-input"
            />
          </div>
        </div>

        <div className="mt-8 flex items-center justify-end gap-3">
          <Button variant="outline" onClick={() => navigate("/app/documents")}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !file}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="upload-submit-btn"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Upload & continue
          </Button>
        </div>
      </div>
    </div>
  );
}
